import { createContext, useContext, useEffect, useState, ReactNode, useCallback, useRef } from 'react';
import { useAuth } from './AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { getPlanByTier, PLANS, type PlanDefinition, type PlanTier } from '@/config/plans';

interface SubscriptionState {
  isLoading: boolean;
  isSubscribed: boolean;
  currentTier: PlanDefinition;
  productId: string | null;
  priceId: string | null;
  subscriptionEnd: string | null;
  cancelAtPeriodEnd: boolean;
}

interface SubscriptionContextType extends SubscriptionState {
  refreshSubscription: () => Promise<void>;
  checkUsageLimit: (type: 'transcripts' | 'analyses' | 'audioMinutes', current: number, prepaidCredits?: number) => { 
    allowed: boolean; 
    limit: number; 
    percentage: number;
    source: 'monthly' | 'prepaid' | 'blocked';
    monthlyRemaining?: number;
    prepaidRemaining?: number;
  };
}

const defaultTier = PLANS.find(t => t.tier === 'free')!;

const CACHE_KEY = 'verithos_sub_cache';
const CACHE_TTL_MS = 10 * 60 * 1000; // 10 minutes

interface CachedSubscription {
  state: SubscriptionState;
  timestamp: number;
}

function readCache(): SubscriptionState | null {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const cached: CachedSubscription = JSON.parse(raw);
    if (Date.now() - cached.timestamp > CACHE_TTL_MS) {
      localStorage.removeItem(CACHE_KEY);
      return null;
    }
    return cached.state;
  } catch {
    return null;
  }
}

function writeCache(state: SubscriptionState) {
  try {
    const cached: CachedSubscription = { state, timestamp: Date.now() };
    localStorage.setItem(CACHE_KEY, JSON.stringify(cached));
  } catch {
    // localStorage full or unavailable
  }
}

const SubscriptionContext = createContext<SubscriptionContextType | undefined>(undefined);

export function SubscriptionProvider({ children }: { children: ReactNode }) {
  const { user, session } = useAuth();
  
  // Initialize from cache for instant render
  const cachedState = useRef(readCache());
  
  const [state, setState] = useState<SubscriptionState>(() => {
    if (cachedState.current) {
      return { ...cachedState.current, isLoading: false };
    }
    return {
      isLoading: true,
      isSubscribed: false,
      currentTier: defaultTier,
      productId: null,
      priceId: null,
      subscriptionEnd: null,
      cancelAtPeriodEnd: false,
    };
  });

  const refreshSubscription = useCallback(async () => {
    const { data: sessionData } = await supabase.auth.getSession();
    const currentSession = sessionData?.session;
    
    if (!currentSession?.access_token) {
      const freeState: SubscriptionState = {
        isLoading: false,
        isSubscribed: false,
        currentTier: defaultTier,
        productId: null,
        priceId: null,
        subscriptionEnd: null,
        cancelAtPeriodEnd: false,
      };
      setState(freeState);
      localStorage.removeItem(CACHE_KEY);
      return;
    }

    try {
      const { data, error } = await supabase.functions.invoke('check-subscription', {
        headers: {
          Authorization: `Bearer ${currentSession.access_token}`,
        },
      });

      if (error) {
        const errorMessage = error?.message || '';
        if (errorMessage.includes('Authentication') || errorMessage.includes('JWT') || errorMessage.includes('token')) {
          console.log('Session expired, waiting for refresh...');
          setState(prev => ({ ...prev, isLoading: false }));
          return;
        }
        console.error('Error checking subscription:', error);
        setState(prev => ({ ...prev, isLoading: false }));
        return;
      }

      const tier = data.tier ? getPlanByTier(data.tier) : defaultTier;

      const newState: SubscriptionState = {
        isLoading: false,
        isSubscribed: data.subscribed,
        currentTier: tier || defaultTier,
        productId: data.productId,
        priceId: data.priceId || null,
        subscriptionEnd: data.subscriptionEnd,
        cancelAtPeriodEnd: data.cancelAtPeriodEnd,
      };
      
      setState(newState);
      writeCache(newState);
    } catch (error) {
      console.error('Error refreshing subscription:', error);
      setState(prev => ({ ...prev, isLoading: false }));
    }
  }, []);

  // On mount with user: if cache was used, refresh in background (non-blocking)
  useEffect(() => {
    if (user) {
      if (cachedState.current) {
        // Cache was used — background refresh, don't block UI
        refreshSubscription();
        cachedState.current = null; // Only use cache once
      } else {
        refreshSubscription();
      }
    } else {
      setState({
        isLoading: false,
        isSubscribed: false,
        currentTier: defaultTier,
        productId: null,
        priceId: null,
        subscriptionEnd: null,
        cancelAtPeriodEnd: false,
      });
      localStorage.removeItem(CACHE_KEY);
    }
  }, [user, refreshSubscription]);

  // Refresh every 5 minutes
  useEffect(() => {
    if (!user) return;
    const interval = setInterval(refreshSubscription, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [user, refreshSubscription]);

  const checkUsageLimit = useCallback((type: 'transcripts' | 'analyses' | 'audioMinutes', current: number, prepaidCredits: number = 0) => {
    let limit: number;
    
    switch (type) {
      case 'transcripts':
        limit = state.currentTier.limits.transcripts;
        break;
      case 'analyses':
        limit = state.currentTier.limits.analysesPerMonth;
        break;
      case 'audioMinutes':
        limit = state.currentTier.limits.audioMinutesPerMonth;
        break;
      default:
        limit = 0;
    }
    
    if (limit === -1) {
      return { allowed: true, limit: -1, percentage: 0, source: 'monthly' as const };
    }
    
    const monthlyRemaining = Math.max(0, limit - current);
    const totalRemaining = monthlyRemaining + prepaidCredits;
    const percentage = Math.round((current / limit) * 100);
    
    if (current < limit) {
      return { 
        allowed: true, 
        limit, 
        percentage: Math.min(percentage, 100),
        source: 'monthly' as const,
        monthlyRemaining,
        prepaidRemaining: prepaidCredits,
      };
    }
    
    if (prepaidCredits > 0) {
      return {
        allowed: true,
        limit,
        percentage: 100,
        source: 'prepaid' as const,
        monthlyRemaining: 0,
        prepaidRemaining: prepaidCredits,
      };
    }
    
    return { 
      allowed: false, 
      limit, 
      percentage: 100,
      source: 'blocked' as const,
      monthlyRemaining: 0,
      prepaidRemaining: 0,
    };
  }, [state.currentTier]);

  return (
    <SubscriptionContext.Provider value={{ ...state, refreshSubscription, checkUsageLimit }}>
      {children}
    </SubscriptionContext.Provider>
  );
}

const fallbackContext: SubscriptionContextType = {
  isLoading: false,
  isSubscribed: false,
  currentTier: defaultTier,
  productId: null,
  priceId: null,
  subscriptionEnd: null,
  cancelAtPeriodEnd: false,
  refreshSubscription: async () => {},
  checkUsageLimit: () => ({ allowed: true, limit: 0, percentage: 0, source: 'blocked' as const }),
};

export function useSubscription() {
  const context = useContext(SubscriptionContext);
  if (context === undefined) {
    return fallbackContext;
  }
  return context;
}
